/**
 * Calculation Log mock data + types.
 *
 * This is the audit-trail surface for every change to a client's knowledge
 * map. Every entry must:
 *  1. Reference its backing-table row (`sourceTable` + `sourceRowId`).
 *  2. Carry an actor + reason code (typed).
 *  3. Be append-only — `status: 'reverted'` is DERIVED from the existence of
 *     a later revert entry pointing at this one (see `deriveEntryStatus`).
 *  4. For replays: point back to the original entry/entries via
 *     `replacesEntryIds`, and the originals MUST themselves be `kind: 'original'`.
 *     This is the anti-hallucination rule — replays never feed replays.
 *
 * Issue #402 ships this surface in mock; the underlying tables
 * (`AttemptEventLog`, `CoachAdjustmentLog`) carry the same audit fields
 * (`kind`, `replayedFromEventId`) for the eventual DB wiring.
 */

import type { CalculationLogEntry } from '../types'

// =============================================================================
// TYPED ENUMS
// =============================================================================

/**
 * Which logical event-class an entry represents. Maps loosely onto a backing
 * table (see `SourceTable`), but is independent so multiple sources can share
 * a table (e.g. coach_adjustment + seed both land in CoachAdjustmentLog).
 */
export type CalculationSource =
  | 'attempt'
  | 'coach_adjustment'
  | 'engine_recompute'
  | 'seed'
  | 'recommendation_lifecycle'

/** Where this entry's audit-trail row actually lives once we wire to real DB. */
export type SourceTable =
  | 'AttemptEventLog'
  | 'CoachAdjustmentLog'
  | 'ClientSubtopicState'
  | 'ClientTopicState'
  | 'ClientRecommendation'

/** Who fired the action. `system` covers engine recomputes and reprocess cascades. */
export type ActorKind = 'student' | 'coach' | 'system'

/**
 * First processing vs. replay-under-new-settings. Mirrors the
 * `CalculationLogEntryKind` schema enum (db/schema/enums.ts).
 */
export type CalculationLogEntryKind = 'original' | 'replay'

/**
 * Typed reason codes. Used both by the in-memory mock entries AND by the
 * eventual `CoachAdjustmentLog.reasonCode` column. Snapshotted in the
 * invariant tests so adding/removing one is deliberate.
 */
export type ReasonCode =
  // student-attempt events (one per FSRS grade)
  | 'student_attempt_easy'
  | 'student_attempt_good'
  | 'student_attempt_hard'
  | 'student_attempt_again'
  // coach adjustments — major (reframe history → reprocess)
  | 'threshold_change'
  | 'rollup_weight_change'
  | 'preset_applied'
  | 'target_company_change'
  | 'baseline_level_change'
  | 'topic_inclusion_change'
  | 'subtopic_enable_toggle'
  // coach adjustments — minor (projection-only)
  | 'weekly_commitment_change'
  | 'target_date_change'
  // coach adjustments — events (not settings)
  | 'attempt_reviewed'
  // seed events
  | 'coach_seed'
  | 'coach_seed_override'
  // engine-emitted events
  | 'fsrs_recompute'
  | 'decay_catchup'
  // audit bookkeeping
  | 'coach_revert'
  | 'coach_reapply'
  | 'coach_revert_to_snapshot'
  | 'reprocess_cascade'
  | 'processing_run_requested'
  | 'settings_snapshot_captured'
  | 'baseline_epoch_change'
  | 'graph_json_saved'

/**
 * Snapshotted list of every legal `ReasonCode`. Imported by the invariant
 * tests as a regression guard — adding or removing a code requires a
 * deliberate test update.
 */
export const REASON_CODES: readonly ReasonCode[] = [
  'student_attempt_easy',
  'student_attempt_good',
  'student_attempt_hard',
  'student_attempt_again',
  'threshold_change',
  'rollup_weight_change',
  'preset_applied',
  'target_company_change',
  'baseline_level_change',
  'topic_inclusion_change',
  'subtopic_enable_toggle',
  'weekly_commitment_change',
  'target_date_change',
  'attempt_reviewed',
  'coach_seed',
  'coach_seed_override',
  'fsrs_recompute',
  'decay_catchup',
  'coach_revert',
  'coach_reapply',
  'coach_revert_to_snapshot',
  'reprocess_cascade',
  'processing_run_requested',
  'settings_snapshot_captured',
  'baseline_epoch_change',
  'graph_json_saved',
] as const

export const CALCULATION_SOURCES: readonly CalculationSource[] = [
  'attempt',
  'coach_adjustment',
  'engine_recompute',
  'seed',
  'recommendation_lifecycle',
] as const

export const SOURCE_TABLES: readonly SourceTable[] = [
  'AttemptEventLog',
  'CoachAdjustmentLog',
  'ClientSubtopicState',
  'ClientTopicState',
  'ClientRecommendation',
] as const

// =============================================================================
// ROW-CHANGE SHAPE (unchanged from prior mock — still the human-readable
// "what numbers moved" payload that the UI renders)
// =============================================================================

export type CalculationTargetKind =
  | 'subtopic'
  | 'topic'
  | 'overall'
  | 'projection'

/** A weight (FSRS parameter or EMA factor) used by a calculation, called out explicitly. */
export interface MockCalculationWeight {
  symbol: string
  value: string
  role: string
}

export interface MockCalculationRowChange {
  /** Whether this row changed a subtopic, a topic rollup, overall mastery, or a projection. */
  targetKind: CalculationTargetKind
  /** Display name of the thing that changed, e.g. "Fixed Window" or "Sliding Window". */
  targetName: string
  field: string
  before: number
  after: number
  /** Human name of the formula, e.g. "FSRS Stability Update". */
  formulaName: string
  /** Why this calculation is run. */
  purpose: string
  equation: string
  /** Weights used, shown separately from generic inputs. */
  weights: MockCalculationWeight[]
  inputs: { name: string; value: string; note: string }[]
  output: string
}

export interface MockSnapshotDiffRow {
  label: string
  previousValue: string
  currentValue: string
}

// =============================================================================
// ENTRY SHAPE — base + source-specific extras
// =============================================================================

/**
 * Fields every entry has, regardless of source. The audit-trail invariants
 * (every field non-empty, parent-child resolves, etc.) are enforced over
 * exactly this set.
 */
export interface BaseCalcLogEntry {
  id: string
  occurredAt: string
  source: CalculationSource
  kind: CalculationLogEntryKind
  /**
   * For `kind: 'replay'` — the originals being reprocessed (1-N).
   * For audit events (`coach_revert`, `coach_reapply`) — the entry being acted on.
   * For `kind: 'original'` non-audit events — always empty.
   *
   * Invariant: every id MUST resolve to an entry in the log, AND every
   * referenced entry MUST itself be `kind: 'original'` (no replay-of-replay).
   */
  replacesEntryIds: string[]
  /** Which table backs this entry (read-side provenance). */
  sourceTable: SourceTable
  /** Stable id of the backing row. In mock: `'<table>:<entry.id>'`. */
  sourceRowId: string
  actorKind: ActorKind
  actorName: string
  reasonCode: ReasonCode
  /** Human-readable explanation of why this entry exists. */
  rationale: string
  /** Short headline shown in the entry's collapsed row. */
  summary: string
  rowsAffected: MockCalculationRowChange[]
  /** Optional snapshot-level diff rows for processing-baseline capture events. */
  snapshotDiff?: MockSnapshotDiffRow[]
}

export interface AttemptCalcLogEntry extends BaseCalcLogEntry {
  source: 'attempt'
  problemSlug: string
  problemSource: string
  grade: 1 | 2 | 3 | 4
  durationMinutes: number
  runCount: number
  submitCount: number
}

export interface CoachAdjustmentCalcLogEntry extends BaseCalcLogEntry {
  source: 'coach_adjustment'
}

export interface EngineRecomputeCalcLogEntry extends BaseCalcLogEntry {
  source: 'engine_recompute'
}

export interface SeedCalcLogEntry extends BaseCalcLogEntry {
  source: 'seed'
}

export interface RecommendationLifecycleCalcLogEntry extends BaseCalcLogEntry {
  source: 'recommendation_lifecycle'
}

export type MockCalculationLogEntry =
  | AttemptCalcLogEntry
  | CoachAdjustmentCalcLogEntry
  | EngineRecomputeCalcLogEntry
  | SeedCalcLogEntry
  | RecommendationLifecycleCalcLogEntry

// =============================================================================
// DERIVED STATE
// =============================================================================

/**
 * "Reverted" / "applied" status is NOT stored on the entry — it's derived
 * from the append-only stream. An entry is `reverted` iff at least one
 * later `coach_revert` carries this entry's id in `replacesEntryIds`.
 *
 * IMPORTANT: a subsequent `coach_reapply` does NOT un-revert the original.
 * `coach_reapply` is its own fresh entry (an "apply this again" event)
 * appended to the log — the original stays reverted forever because that
 * IS its history. This is the architectural pillar that makes the calc
 * log's "no hallucinatory data" contract enforceable: nothing in the past
 * is ever mutated, and "reverted" is a monotonically irreversible label
 * on a specific historical row.
 */
export type CalculationStatus = 'applied' | 'reverted'

export function deriveEntryStatus(
  entry: MockCalculationLogEntry,
  allEntries: readonly MockCalculationLogEntry[],
): CalculationStatus {
  const entryTs = new Date(entry.occurredAt).getTime()
  for (const candidate of allEntries) {
    if (candidate.reasonCode !== 'coach_revert') continue
    if (!candidate.replacesEntryIds.includes(entry.id)) continue
    if (new Date(candidate.occurredAt).getTime() <= entryTs) continue
    return 'reverted'
  }
  return 'applied'
}

// =============================================================================
// FACTORY
// =============================================================================

/**
 * Assigns id/occurredAt to a cascade-built partial entry. The mock store
 * uses this to spin up new entries from action handlers; the `id` becomes
 * the stable handle the UI keys off of.
 *
 * The cast is safe: spreading preserves every discriminant field on the
 * incoming partial, including `source`. We can't express that through TS's
 * `Omit<Union, K>` because it collapses the union — so the runtime
 * guarantee is enforced by callers building proper source-tagged partials.
 */
export function makeCalculationLogEntry(
  partial: Omit<MockCalculationLogEntry, 'id' | 'occurredAt'>,
): MockCalculationLogEntry {
  return {
    ...partial,
    id: `calc_${crypto.randomUUID()}`,
    occurredAt: new Date().toISOString(),
  } as MockCalculationLogEntry
}

// =============================================================================
// PRE-AUTHORED MOCK ENTRIES
// =============================================================================

/**
 * Helper used only inside this file to keep the pre-authored entries below
 * compact and readable. Builds the audit fields from the entry id.
 *
 * `sourceTable` is inferred from `source`: attempts live in `AttemptEventLog`,
 * everything else in `CoachAdjustmentLog`.
 */
function audit(
  id: string,
  source: CalculationSource,
): Pick<BaseCalcLogEntry, 'sourceTable' | 'sourceRowId'> {
  const sourceTable: SourceTable =
    source === 'attempt' ? 'AttemptEventLog' : 'CoachAdjustmentLog'
  return { sourceTable, sourceRowId: `${sourceTable}:${id}` }
}

export const MOCK_CALCULATION_LOG: CalculationLogEntry[] = [
  // calc_1: Easy grade on Fixed Window
  {
    id: 'calc_1',
    occurredAt: '2026-05-13T18:20:00Z',
    source: 'attempt',
    kind: 'original',
    replacesEntryIds: [],
    ...audit('calc_1', 'attempt'),
    actorKind: 'student',
    actorName: 'Jordan Lee',
    reasonCode: 'student_attempt_easy',
    rationale: 'Student submitted an attempt graded Easy on Fixed Window.',
    summary:
      'Easy grade on Fixed Window — stability and mastery rose; topic rollup, overall mastery, and projection recomputed.',
    problemSlug: 'maximum-sum-subarray-of-size-k',
    problemSource: 'BCtCI',
    grade: 4,
    durationMinutes: 14,
    runCount: 3,
    submitCount: 1,
    rowsAffected: [
      {
        targetKind: 'subtopic',
        targetName: 'Fixed Window',
        field: 'stabilityDays',
        before: 52,
        after: 60,
        formulaName: 'FSRS Stability Update',
        purpose:
          'Extends how long the skill stays retrievable after a successful review.',
        equation:
          "S' = S × (1 + e^w8 · (11 − D) · S^−w9 · (e^(w10·(1−R)) − 1) · b)",
        weights: [
          { symbol: 'w8', value: '0.27', role: 'stability-growth exponent' },
          {
            symbol: 'w9',
            value: '0.44',
            role: 'stability-saturation exponent',
          },
          {
            symbol: 'w10',
            value: '0.90',
            role: 'retrievability-gap multiplier',
          },
          { symbol: 'b', value: '1.20', role: 'Easy-grade bonus multiplier' },
        ],
        inputs: [
          { name: 'S', value: '52', note: 'stability before the review' },
          { name: 'D', value: '4.5', note: 'difficulty before the review' },
          { name: 'R', value: '0.93', note: 'retrievability at review time' },
        ],
        output: 'S = 60.0 days',
      },
      {
        targetKind: 'subtopic',
        targetName: 'Fixed Window',
        field: 'difficulty',
        before: 4.6,
        after: 4.5,
        formulaName: 'FSRS Difficulty Update',
        purpose:
          'Eases difficulty slightly after a confident solve, mean-reverting toward 5.',
        equation: "D' = D + ΔD(grade) · (10 − D) / 9, then mean-reverted",
        weights: [
          {
            symbol: 'ΔD(4)',
            value: '−0.20',
            role: 'Easy-grade difficulty delta',
          },
        ],
        inputs: [
          { name: 'D', value: '4.6', note: 'difficulty before the review' },
        ],
        output: 'D = 4.5',
      },
      {
        targetKind: 'subtopic',
        targetName: 'Fixed Window',
        field: 'mastery',
        before: 83,
        after: 85,
        formulaName: 'EMA Mastery Update',
        purpose:
          'Nudges the slow competence ceiling toward the latest attempt score.',
        equation: "mastery' = (1 − α) · mastery + α · attemptScore",
        weights: [{ symbol: 'α', value: '0.15', role: 'EMA smoothing factor' }],
        inputs: [
          {
            name: 'mastery',
            value: '83',
            note: 'mastery before the attempt',
          },
          {
            name: 'attemptScore',
            value: '100',
            note: 'Easy grade → score 100',
          },
        ],
        output: 'mastery = 85.0',
      },
      {
        targetKind: 'topic',
        targetName: 'Sliding Window',
        field: 'topicMastery',
        before: 50,
        after: 52,
        formulaName: 'Topic Mastery Rollup',
        purpose:
          'Re-aggregates the topic from its subtopics, weighting weaker ones more.',
        equation:
          'topicMastery = Σ(wᵢ · effectiveMasteryᵢ) / Σ(wᵢ),  wᵢ = 101 − effectiveMasteryᵢ',
        weights: [
          {
            symbol: 'wᵢ',
            value: '101 − EMᵢ',
            role: 'weakness weight per subtopic',
          },
        ],
        inputs: [
          {
            name: 'subtopics',
            value: 'Fixed / Dynamic / Resetting',
            note: 'effective mastery 81 / 25 / 18',
          },
        ],
        output: 'topicMastery = 52.0',
      },
      {
        targetKind: 'overall',
        targetName: 'Overall mastery',
        field: 'overallMastery',
        before: 52,
        after: 53,
        formulaName: 'Overall Mastery Rollup',
        purpose:
          'Averages topic mastery across all topics for the headline progress number.',
        equation: 'overallMastery = mean(topicMasteryᵢ over all topics)',
        weights: [],
        inputs: [
          {
            name: 'topics',
            value: '29',
            note: 'all topics included in the rollup',
          },
        ],
        output: 'overallMastery = 53',
      },
      {
        targetKind: 'projection',
        targetName: 'Time to mastery',
        field: 'estimatedDays',
        before: 88,
        after: 86,
        formulaName: 'Time-to-Mastery Projection',
        purpose:
          'Re-estimates plan length from the remaining mastery gap and weekly commitment.',
        equation: 'days = ceil((Σ gapᵢ · hoursPerGapPoint) / weeklyHours · 7)',
        weights: [
          {
            symbol: 'hoursPerGapPoint',
            value: '0.5',
            role: 'tunable effort constant',
          },
        ],
        inputs: [
          {
            name: 'Σ gap',
            value: '604',
            note: 'summed mastery gap across included topics',
          },
          {
            name: 'weeklyHours',
            value: '10',
            note: 'client weekly commitment',
          },
        ],
        output: 'estimatedDays = 86',
      },
    ],
  },

  // calc_2: Hard grade on Lower / Upper Bound
  {
    id: 'calc_2',
    occurredAt: '2026-05-12T17:05:00Z',
    source: 'attempt',
    kind: 'original',
    replacesEntryIds: [],
    ...audit('calc_2', 'attempt'),
    actorKind: 'student',
    actorName: 'Jordan Lee',
    reasonCode: 'student_attempt_hard',
    rationale:
      'Student submitted an attempt graded Hard on Lower / Upper Bound.',
    summary:
      'Hard grade on Lower / Upper Bound — small stability gain, difficulty rose; topic rollup, overall mastery, and projection recomputed.',
    problemSlug: 'binary-search-lower-bound',
    problemSource: 'BCtCI',
    grade: 2,
    durationMinutes: 41,
    runCount: 9,
    submitCount: 4,
    rowsAffected: [
      {
        targetKind: 'subtopic',
        targetName: 'Lower / Upper Bound',
        field: 'stabilityDays',
        before: 12,
        after: 14,
        formulaName: 'FSRS Stability Update',
        purpose:
          'Extends stability modestly after a successful-but-effortful review.',
        equation:
          "S' = S × (1 + e^w8 · (11 − D) · S^−w9 · (e^(w10·(1−R)) − 1) · h)",
        weights: [
          { symbol: 'w8', value: '0.27', role: 'stability-growth exponent' },
          {
            symbol: 'w9',
            value: '0.44',
            role: 'stability-saturation exponent',
          },
          {
            symbol: 'w10',
            value: '0.90',
            role: 'retrievability-gap multiplier',
          },
          {
            symbol: 'h',
            value: '0.80',
            role: 'Hard-grade penalty multiplier',
          },
        ],
        inputs: [
          { name: 'S', value: '12', note: 'stability before the review' },
          { name: 'D', value: '5.2', note: 'difficulty before the review' },
          { name: 'R', value: '0.71', note: 'retrievability at review time' },
        ],
        output: 'S = 14.0 days',
      },
      {
        targetKind: 'subtopic',
        targetName: 'Lower / Upper Bound',
        field: 'difficulty',
        before: 5.2,
        after: 5.5,
        formulaName: 'FSRS Difficulty Update',
        purpose:
          'Raises difficulty after a hard solve, signalling the item is genuinely challenging.',
        equation: "D' = D + ΔD(grade) · (10 − D) / 9, then mean-reverted",
        weights: [
          {
            symbol: 'ΔD(2)',
            value: '+0.60',
            role: 'Hard-grade difficulty delta',
          },
        ],
        inputs: [
          { name: 'D', value: '5.2', note: 'difficulty before the review' },
        ],
        output: 'D = 5.5',
      },
      {
        targetKind: 'subtopic',
        targetName: 'Lower / Upper Bound',
        field: 'mastery',
        before: 55,
        after: 56,
        formulaName: 'EMA Mastery Update',
        purpose:
          'Nudges mastery upward slightly — the Hard grade maps to a lower attempt score.',
        equation: "mastery' = (1 − α) · mastery + α · attemptScore",
        weights: [{ symbol: 'α', value: '0.15', role: 'EMA smoothing factor' }],
        inputs: [
          {
            name: 'mastery',
            value: '55',
            note: 'mastery before the attempt',
          },
          {
            name: 'attemptScore',
            value: '60',
            note: 'Hard grade → score 60',
          },
        ],
        output: 'mastery = 55.75 ≈ 56',
      },
      {
        targetKind: 'topic',
        targetName: 'Binary Search',
        field: 'topicMastery',
        before: 61,
        after: 62,
        formulaName: 'Topic Mastery Rollup',
        purpose:
          'Re-aggregates the Binary Search topic from its subtopics, weighting weaker ones more.',
        equation:
          'topicMastery = Σ(wᵢ · effectiveMasteryᵢ) / Σ(wᵢ),  wᵢ = 101 − effectiveMasteryᵢ',
        weights: [
          {
            symbol: 'wᵢ',
            value: '101 − EMᵢ',
            role: 'weakness weight per subtopic',
          },
        ],
        inputs: [
          {
            name: 'subtopics',
            value: 'Binary Search / Lower-Upper / Rotated',
            note: 'effective mastery 72 / 56 / 44',
          },
        ],
        output: 'topicMastery = 62.0',
      },
      {
        targetKind: 'overall',
        targetName: 'Overall mastery',
        field: 'overallMastery',
        before: 51,
        after: 52,
        formulaName: 'Overall Mastery Rollup',
        purpose:
          'Averages topic mastery across all topics for the headline progress number.',
        equation: 'overallMastery = mean(topicMasteryᵢ over all topics)',
        weights: [],
        inputs: [
          {
            name: 'topics',
            value: '29',
            note: 'all topics included in the rollup',
          },
        ],
        output: 'overallMastery = 52',
      },
      {
        targetKind: 'projection',
        targetName: 'Time to mastery',
        field: 'estimatedDays',
        before: 91,
        after: 88,
        formulaName: 'Time-to-Mastery Projection',
        purpose:
          'Re-estimates plan length from the remaining mastery gap and weekly commitment.',
        equation: 'days = ceil((Σ gapᵢ · hoursPerGapPoint) / weeklyHours · 7)',
        weights: [
          {
            symbol: 'hoursPerGapPoint',
            value: '0.5',
            role: 'tunable effort constant',
          },
        ],
        inputs: [
          {
            name: 'Σ gap',
            value: '616',
            note: 'summed mastery gap across included topics',
          },
          {
            name: 'weeklyHours',
            value: '10',
            note: 'client weekly commitment',
          },
        ],
        output: 'estimatedDays = 88',
      },
    ],
  },

  // calc_3: Good grade on Amortized Append
  {
    id: 'calc_3',
    occurredAt: '2026-05-12T09:30:00Z',
    source: 'attempt',
    kind: 'original',
    replacesEntryIds: [],
    ...audit('calc_3', 'attempt'),
    actorKind: 'student',
    actorName: 'Jordan Lee',
    reasonCode: 'student_attempt_good',
    rationale: 'Student submitted an attempt graded Good on Amortized Append.',
    summary:
      'Good grade on Amortized Append — steady stability gain, mastery nudged up; topic rollup, overall mastery, and projection recomputed.',
    problemSlug: 'rotate-array-in-place',
    problemSource: 'BCtCI',
    grade: 3,
    durationMinutes: 19,
    runCount: 4,
    submitCount: 2,
    rowsAffected: [
      {
        targetKind: 'subtopic',
        targetName: 'Amortized Append',
        field: 'stabilityDays',
        before: 48,
        after: 60,
        formulaName: 'FSRS Stability Update',
        purpose:
          'Extends stability after a comfortable review — no bonus or penalty multiplier for Good.',
        equation:
          "S' = S × (1 + e^w8 · (11 − D) · S^−w9 · (e^(w10·(1−R)) − 1))",
        weights: [
          { symbol: 'w8', value: '0.27', role: 'stability-growth exponent' },
          {
            symbol: 'w9',
            value: '0.44',
            role: 'stability-saturation exponent',
          },
          {
            symbol: 'w10',
            value: '0.90',
            role: 'retrievability-gap multiplier',
          },
        ],
        inputs: [
          { name: 'S', value: '48', note: 'stability before the review' },
          { name: 'D', value: '3.5', note: 'difficulty before the review' },
          { name: 'R', value: '0.88', note: 'retrievability at review time' },
        ],
        output: 'S = 60.0 days',
      },
      {
        targetKind: 'subtopic',
        targetName: 'Amortized Append',
        field: 'difficulty',
        before: 3.5,
        after: 3.4,
        formulaName: 'FSRS Difficulty Update',
        purpose:
          'Eases difficulty marginally after a Good solve, mean-reverting toward 5.',
        equation: "D' = D + ΔD(grade) · (10 − D) / 9, then mean-reverted",
        weights: [
          {
            symbol: 'ΔD(3)',
            value: '−0.10',
            role: 'Good-grade difficulty delta',
          },
        ],
        inputs: [
          { name: 'D', value: '3.5', note: 'difficulty before the review' },
        ],
        output: 'D = 3.4',
      },
      {
        targetKind: 'subtopic',
        targetName: 'Amortized Append',
        field: 'mastery',
        before: 74,
        after: 76,
        formulaName: 'EMA Mastery Update',
        purpose:
          'Nudges mastery upward after a confident pass — Good grade maps to score 80.',
        equation: "mastery' = (1 − α) · mastery + α · attemptScore",
        weights: [{ symbol: 'α', value: '0.15', role: 'EMA smoothing factor' }],
        inputs: [
          {
            name: 'mastery',
            value: '74',
            note: 'mastery before the attempt',
          },
          {
            name: 'attemptScore',
            value: '80',
            note: 'Good grade → score 80',
          },
        ],
        output: 'mastery = 74.9 ≈ 75 → 76 after rounding',
      },
      {
        targetKind: 'topic',
        targetName: 'Dynamic Arrays',
        field: 'topicMastery',
        before: 68,
        after: 70,
        formulaName: 'Topic Mastery Rollup',
        purpose:
          'Re-aggregates the Dynamic Arrays topic from its subtopics, weighting weaker ones more.',
        equation:
          'topicMastery = Σ(wᵢ · effectiveMasteryᵢ) / Σ(wᵢ),  wᵢ = 101 − effectiveMasteryᵢ',
        weights: [
          {
            symbol: 'wᵢ',
            value: '101 − EMᵢ',
            role: 'weakness weight per subtopic',
          },
        ],
        inputs: [
          {
            name: 'subtopics',
            value: 'Amortized Append / Resizing / Index Access',
            note: 'effective mastery 76 / 65 / 88',
          },
        ],
        output: 'topicMastery = 70.0',
      },
      {
        targetKind: 'overall',
        targetName: 'Overall mastery',
        field: 'overallMastery',
        before: 50,
        after: 51,
        formulaName: 'Overall Mastery Rollup',
        purpose:
          'Averages topic mastery across all topics for the headline progress number.',
        equation: 'overallMastery = mean(topicMasteryᵢ over all topics)',
        weights: [],
        inputs: [
          {
            name: 'topics',
            value: '29',
            note: 'all topics included in the rollup',
          },
        ],
        output: 'overallMastery = 51',
      },
      {
        targetKind: 'projection',
        targetName: 'Time to mastery',
        field: 'estimatedDays',
        before: 94,
        after: 91,
        formulaName: 'Time-to-Mastery Projection',
        purpose:
          'Re-estimates plan length from the remaining mastery gap and weekly commitment.',
        equation: 'days = ceil((Σ gapᵢ · hoursPerGapPoint) / weeklyHours · 7)',
        weights: [
          {
            symbol: 'hoursPerGapPoint',
            value: '0.5',
            role: 'tunable effort constant',
          },
        ],
        inputs: [
          {
            name: 'Σ gap',
            value: '629',
            note: 'summed mastery gap across included topics',
          },
          {
            name: 'weeklyHours',
            value: '10',
            note: 'client weekly commitment',
          },
        ],
        output: 'estimatedDays = 91',
      },
    ],
  },

  // calc_4: Again grade on Union Find
  {
    id: 'calc_4',
    occurredAt: '2026-05-11T20:15:00Z',
    source: 'attempt',
    kind: 'original',
    replacesEntryIds: [],
    ...audit('calc_4', 'attempt'),
    actorKind: 'student',
    actorName: 'Jordan Lee',
    reasonCode: 'student_attempt_again',
    rationale: 'Student submitted an attempt graded Again on Union Find.',
    summary:
      'Again grade on Union Find — stability dropped, difficulty rose, mastery fell; topic rollup, overall mastery, and projection recomputed.',
    problemSlug: 'union-find-basic',
    problemSource: 'BCtCI',
    grade: 1,
    durationMinutes: 52,
    runCount: 12,
    submitCount: 6,
    rowsAffected: [
      {
        targetKind: 'subtopic',
        targetName: 'Union Find',
        field: 'stabilityDays',
        before: 8,
        after: 1,
        formulaName: 'FSRS Stability Update',
        purpose:
          'Resets stability after a lapse — the skill has not been retained.',
        equation: "S' = w11 · D^−w12 · ((S + 1)^w13 − 1)",
        weights: [
          {
            symbol: 'w11',
            value: '0.56',
            role: 'post-lapse stability base',
          },
          {
            symbol: 'w12',
            value: '0.23',
            role: 'difficulty scaling exponent',
          },
          {
            symbol: 'w13',
            value: '0.44',
            role: 'pre-lapse stability scaling',
          },
        ],
        inputs: [
          { name: 'S', value: '8', note: 'stability before the lapse' },
          {
            name: 'D',
            value: '6.1',
            note: 'difficulty before the review',
          },
        ],
        output: 'S = 1.0 days (lapse reset)',
      },
      {
        targetKind: 'subtopic',
        targetName: 'Union Find',
        field: 'difficulty',
        before: 6.1,
        after: 6.6,
        formulaName: 'FSRS Difficulty Update',
        purpose:
          'Raises difficulty after a failed recall, signalling the item is very hard.',
        equation: "D' = D + ΔD(grade) · (10 − D) / 9, then mean-reverted",
        weights: [
          {
            symbol: 'ΔD(1)',
            value: '+1.30',
            role: 'Again-grade difficulty delta',
          },
        ],
        inputs: [
          { name: 'D', value: '6.1', note: 'difficulty before the review' },
        ],
        output: 'D = 6.6',
      },
      {
        targetKind: 'subtopic',
        targetName: 'Union Find',
        field: 'mastery',
        before: 38,
        after: 34,
        formulaName: 'EMA Mastery Update',
        purpose:
          'Pulls mastery downward after a failed attempt — Again grade maps to score 0.',
        equation: "mastery' = (1 − α) · mastery + α · attemptScore",
        weights: [{ symbol: 'α', value: '0.15', role: 'EMA smoothing factor' }],
        inputs: [
          {
            name: 'mastery',
            value: '38',
            note: 'mastery before the attempt',
          },
          {
            name: 'attemptScore',
            value: '0',
            note: 'Again grade → score 0',
          },
        ],
        output: 'mastery = 32.3 ≈ 34 after floor',
      },
      {
        targetKind: 'topic',
        targetName: 'Union Find',
        field: 'topicMastery',
        before: 38,
        after: 34,
        formulaName: 'Topic Mastery Rollup',
        purpose:
          'Re-aggregates the Union Find topic; a lower subtopic mastery pulls the topic down.',
        equation:
          'topicMastery = Σ(wᵢ · effectiveMasteryᵢ) / Σ(wᵢ),  wᵢ = 101 − effectiveMasteryᵢ',
        weights: [
          {
            symbol: 'wᵢ',
            value: '101 − EMᵢ',
            role: 'weakness weight per subtopic',
          },
        ],
        inputs: [
          {
            name: 'subtopics',
            value: 'Union Find / Path Compression / Union by Rank',
            note: 'effective mastery 34 / 42 / 30',
          },
        ],
        output: 'topicMastery = 34.0',
      },
      {
        targetKind: 'overall',
        targetName: 'Overall mastery',
        field: 'overallMastery',
        before: 49,
        after: 48,
        formulaName: 'Overall Mastery Rollup',
        purpose:
          'Averages topic mastery across all topics for the headline progress number.',
        equation: 'overallMastery = mean(topicMasteryᵢ over all topics)',
        weights: [],
        inputs: [
          {
            name: 'topics',
            value: '29',
            note: 'all topics included in the rollup',
          },
        ],
        output: 'overallMastery = 48',
      },
      {
        targetKind: 'projection',
        targetName: 'Time to mastery',
        field: 'estimatedDays',
        before: 97,
        after: 99,
        formulaName: 'Time-to-Mastery Projection',
        purpose:
          'Re-estimates plan length — a mastery drop widens the remaining gap.',
        equation: 'days = ceil((Σ gapᵢ · hoursPerGapPoint) / weeklyHours · 7)',
        weights: [
          {
            symbol: 'hoursPerGapPoint',
            value: '0.5',
            role: 'tunable effort constant',
          },
        ],
        inputs: [
          {
            name: 'Σ gap',
            value: '669',
            note: 'summed mastery gap across included topics',
          },
          {
            name: 'weeklyHours',
            value: '10',
            note: 'client weekly commitment',
          },
        ],
        output: 'estimatedDays = 99',
      },
    ],
  },

  // calc_5: Coach raised Binary Search proficiency goal 80 → 85
  {
    id: 'calc_5',
    occurredAt: '2026-05-10T11:00:00Z',
    source: 'coach_adjustment',
    kind: 'original',
    replacesEntryIds: [],
    ...audit('calc_5', 'coach_adjustment'),
    actorKind: 'coach',
    actorName: 'Mike Mroczka (coach)',
    reasonCode: 'threshold_change',
    rationale:
      'Coach raised the Binary Search proficiency goal from 80 to 85 after switching the target company to Meta.',
    summary:
      'Binary Search good-enough threshold raised 80 → 85 for Meta prep — projection recomputed.',
    rowsAffected: [
      {
        targetKind: 'topic',
        targetName: 'Binary Search',
        field: 'minProficiencyThreshold',
        before: 80,
        after: 85,
        formulaName: 'Manual Coach Override',
        purpose:
          'Coach changed the per-topic proficiency goal the engine treats as "good enough".',
        equation: 'manual coach override',
        weights: [],
        inputs: [
          {
            name: 'reason',
            value: 'Target company → Meta',
            note: 'Meta preset recommends 85 for Binary Search',
          },
        ],
        output: 'minProficiencyThreshold = 85',
      },
      {
        targetKind: 'projection',
        targetName: 'Time to mastery',
        field: 'estimatedDays',
        before: 97,
        after: 99,
        formulaName: 'Time-to-Mastery Projection',
        purpose:
          'A higher goal widens the remaining gap for Binary Search, lengthening the projected plan.',
        equation: 'days = ceil((Σ gapᵢ · hoursPerGapPoint) / weeklyHours · 7)',
        weights: [
          {
            symbol: 'hoursPerGapPoint',
            value: '0.5',
            role: 'tunable effort constant',
          },
        ],
        inputs: [
          {
            name: 'Σ gap',
            value: 'recomputed with threshold = 85',
            note: 'summed mastery gap across included topics',
          },
          {
            name: 'weeklyHours',
            value: '12',
            note: 'client weekly commitment at the time',
          },
        ],
        output: 'estimatedDays = 99',
      },
    ],
  },

  // calc_6: Coach lowered weekly commitment 12h → 10h
  {
    id: 'calc_6',
    occurredAt: '2026-05-10T10:30:00Z',
    source: 'coach_adjustment',
    kind: 'original',
    replacesEntryIds: [],
    ...audit('calc_6', 'coach_adjustment'),
    actorKind: 'coach',
    actorName: 'Mike Mroczka (coach)',
    reasonCode: 'weekly_commitment_change',
    rationale: 'Coach lowered the client weekly commitment from 12 h to 10 h.',
    summary:
      'Weekly commitment lowered 12 h → 10 h — projection recomputed; plan length grew.',
    rowsAffected: [
      {
        targetKind: 'projection',
        targetName: 'Time to mastery',
        field: 'estimatedDays',
        before: 81,
        after: 97,
        formulaName: 'Time-to-Mastery Projection',
        purpose:
          'Fewer weekly hours stretches the same remaining work over more calendar days.',
        equation: 'days = ceil((Σ gapᵢ · hoursPerGapPoint) / weeklyHours · 7)',
        weights: [
          {
            symbol: 'hoursPerGapPoint',
            value: '0.5',
            role: 'tunable effort constant',
          },
        ],
        inputs: [
          {
            name: 'weeklyHours',
            value: '10',
            note: 'updated client weekly commitment',
          },
          {
            name: 'Σ gap',
            value: '669',
            note: 'summed mastery gap across included topics',
          },
        ],
        output: 'estimatedDays = 97',
      },
    ],
  },

  // calc_7: Coach excluded Trie from the study plan
  {
    id: 'calc_7',
    occurredAt: '2026-05-08T15:45:00Z',
    source: 'coach_adjustment',
    kind: 'original',
    replacesEntryIds: [],
    ...audit('calc_7', 'coach_adjustment'),
    actorKind: 'coach',
    actorName: 'Dana Brooks (coach)',
    reasonCode: 'topic_inclusion_change',
    rationale:
      'Coach excluded "Trie" from the study plan — not in scope for the target role.',
    summary:
      'Trie excluded from the study plan — overall mastery recomputed without Trie; projection shortened.',
    rowsAffected: [
      {
        targetKind: 'overall',
        targetName: 'Overall mastery',
        field: 'overallMastery',
        before: 47,
        after: 49,
        formulaName: 'Overall Mastery Rollup',
        purpose:
          'Recomputes the headline number excluding Trie (which had low mastery), raising the average.',
        equation: 'overallMastery = mean(topicMasteryᵢ over all topics)',
        weights: [],
        inputs: [
          {
            name: 'topics',
            value: '28 (Trie excluded)',
            note: 'Trie removed from the included set',
          },
        ],
        output: 'overallMastery = 49',
      },
      {
        targetKind: 'projection',
        targetName: 'Time to mastery',
        field: 'estimatedDays',
        before: 88,
        after: 81,
        formulaName: 'Time-to-Mastery Projection',
        purpose:
          'Excluding Trie removes its mastery gap from the total, shortening the plan.',
        equation: 'days = ceil((Σ gapᵢ · hoursPerGapPoint) / weeklyHours · 7)',
        weights: [
          {
            symbol: 'hoursPerGapPoint',
            value: '0.5',
            role: 'tunable effort constant',
          },
        ],
        inputs: [
          {
            name: 'Σ gap',
            value: '594 (Trie gap removed)',
            note: 'summed mastery gap across included topics',
          },
          {
            name: 'weeklyHours',
            value: '12',
            note: 'client weekly commitment at the time',
          },
        ],
        output: 'estimatedDays = 81',
      },
    ],
  },

  // calc_8: Coach applied the "Google" company preset
  {
    id: 'calc_8',
    occurredAt: '2026-05-07T09:00:00Z',
    source: 'coach_adjustment',
    kind: 'original',
    replacesEntryIds: [],
    ...audit('calc_8', 'coach_adjustment'),
    actorKind: 'coach',
    actorName: 'Dana Brooks (coach)',
    reasonCode: 'preset_applied',
    rationale:
      'Coach applied the "Google" company preset — bulk-updates proficiency goals for high-signal topics.',
    summary:
      'Google preset applied — three topic proficiency goals updated; projection recomputed.',
    rowsAffected: [
      {
        targetKind: 'topic',
        targetName: 'Dynamic Programming',
        field: 'minProficiencyThreshold',
        before: 75,
        after: 90,
        formulaName: 'Manual Coach Override',
        purpose:
          'Google preset sets a higher bar for Dynamic Programming interviews.',
        equation: 'manual coach override',
        weights: [],
        inputs: [
          {
            name: 'preset',
            value: 'Google',
            note: 'company proficiency preset',
          },
        ],
        output: 'minProficiencyThreshold = 90',
      },
      {
        targetKind: 'topic',
        targetName: 'Graph Traversal',
        field: 'minProficiencyThreshold',
        before: 75,
        after: 85,
        formulaName: 'Manual Coach Override',
        purpose:
          'Google preset sets a higher bar for Graph Traversal interviews.',
        equation: 'manual coach override',
        weights: [],
        inputs: [
          {
            name: 'preset',
            value: 'Google',
            note: 'company proficiency preset',
          },
        ],
        output: 'minProficiencyThreshold = 85',
      },
      {
        targetKind: 'topic',
        targetName: 'Sliding Window',
        field: 'minProficiencyThreshold',
        before: 70,
        after: 80,
        formulaName: 'Manual Coach Override',
        purpose:
          'Google preset sets a moderate bar for Sliding Window problems.',
        equation: 'manual coach override',
        weights: [],
        inputs: [
          {
            name: 'preset',
            value: 'Google',
            note: 'company proficiency preset',
          },
        ],
        output: 'minProficiencyThreshold = 80',
      },
      {
        targetKind: 'projection',
        targetName: 'Time to mastery',
        field: 'estimatedDays',
        before: 72,
        after: 88,
        formulaName: 'Time-to-Mastery Projection',
        purpose:
          'Higher thresholds from the Google preset widen the total remaining gap.',
        equation: 'days = ceil((Σ gapᵢ · hoursPerGapPoint) / weeklyHours · 7)',
        weights: [
          {
            symbol: 'hoursPerGapPoint',
            value: '0.5',
            role: 'tunable effort constant',
          },
        ],
        inputs: [
          {
            name: 'Σ gap',
            value: '594 (thresholds raised)',
            note: 'summed mastery gap across included topics',
          },
          {
            name: 'weeklyHours',
            value: '12',
            note: 'client weekly commitment at the time',
          },
        ],
        output: 'estimatedDays = 88',
      },
    ],
  },
]
